<script setup>
/* eslint-disable no-use-before-define */
import { computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import AnnouncementCarousel from './AnnouncementCarousel.vue';

const props = defineProps({
  accountName: {
    type: String,
    default: 'Talkvia',
  },
  operatorName: {
    type: String,
    default: 'time',
  },
  hasCorbanModule: {
    type: Boolean,
    default: false,
  },
});

const { accountScopedRoute } = useAccount();
const currentRole = useMapGetter('getCurrentRole');
const currentUser = useMapGetter('getCurrentUser');

const canManageCorban = computed(
  () =>
    currentUser.value?.type === 'SuperAdmin' ||
    currentRole.value === 'administrator'
);

const headline = computed(() =>
  props.hasCorbanModule
    ? `Olá, ${props.operatorName}. Falta conectar o Corban.`
    : `Olá, ${props.operatorName}. Sua operação começa pelos atendimentos.`
);

const description = computed(() =>
  props.hasCorbanModule
    ? 'Enquanto a integração Corban não é configurada, acompanhe os canais, contatos e pendências comerciais por aqui.'
    : 'Esta conta não usa módulos Corban. Use esta visão para organizar conversas, canais e rotina operacional sem depender do funil de crédito.'
);

const primaryAction = computed(() => {
  if (props.hasCorbanModule && canManageCorban.value) {
    return {
      label: 'Configurar Corban',
      icon: 'i-lucide-plug-zap',
      to: accountScopedRoute('settings_applications'),
    };
  }

  return {
    label: 'Abrir atendimentos',
    icon: 'i-lucide-message-circle',
    to: accountScopedRoute('talkvia_conversations'),
  };
});

const workspaceCards = computed(() => [
  {
    label: 'Atendimentos',
    value: 'Conversas',
    detail: 'Retome novos contatos, menções e conversas pendentes.',
    icon: 'i-lucide-message-circle',
    tone: 'blue',
    to: accountScopedRoute('talkvia_conversations'),
  },
  {
    label: 'Contatos',
    value: 'Base ativa',
    detail: 'Consulte clientes e histórico antes de avançar uma negociação.',
    icon: 'i-lucide-users-round',
    tone: 'teal',
    to: accountScopedRoute('contacts_dashboard_index', {}, { page: 1 }),
  },
  {
    label: 'Agenda',
    value: 'Retornos',
    detail: 'Organize compromissos, follow-ups e próximos passos do time.',
    icon: 'i-lucide-calendar-days',
    tone: 'amber',
    to: accountScopedRoute('talkvia_calendar'),
  },
]);

const setupItems = computed(() => [
  {
    label: 'Canais de entrada',
    detail:
      'Confirme WhatsApp, site, email ou API antes de distribuir a operação.',
    icon: 'i-lucide-inbox',
    to: accountScopedRoute('settings_inbox_list'),
  },
  {
    label: 'Integrações',
    detail:
      'Conecte serviços externos quando a conta precisar de automações ou contexto adicional.',
    icon: 'i-lucide-plug',
    to: accountScopedRoute('settings_applications'),
    adminOnly: false,
  },
  {
    label: 'Equipe',
    detail:
      'Revise agentes, papéis e responsáveis para manter a fila saudável.',
    icon: 'i-lucide-user-cog',
    to: accountScopedRoute('agent_list'),
    adminOnly: true,
  },
]);

const visibleSetupItems = computed(() =>
  setupItems.value.filter(item => !item.adminOnly || canManageCorban.value)
);

function toneClass(tone) {
  return {
    blue: 'bg-n-blue-3 text-n-blue-11',
    teal: 'bg-n-teal-3 text-n-teal-11',
    amber: 'bg-n-amber-3 text-n-amber-11',
    slate: 'bg-n-slate-3 text-n-slate-11',
  }[tone];
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <main class="h-full overflow-y-auto bg-n-background">
    <section class="border-b border-n-weak px-6 py-6">
      <div
        class="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between"
      >
        <div class="max-w-3xl">
          <div class="mb-3 flex items-center gap-2 text-sm text-n-slate-10">
            <span
              class="grid size-7 place-items-center rounded-lg bg-n-blue-3 text-n-blue-11"
            >
              <span class="i-lucide-house size-4" />
            </span>
            Home · {{ accountName }}
          </div>
          <h1 class="mb-2 text-2xl font-semibold text-n-slate-12">
            {{ headline }}
          </h1>
          <p class="mb-0 text-sm leading-6 text-n-slate-11">
            {{ description }}
          </p>
        </div>

        <router-link
          :to="primaryAction.to"
          class="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-n-blue-9 px-4 text-sm font-medium text-white hover:bg-n-blue-10"
        >
          <span class="size-4" :class="primaryAction.icon" />
          {{ primaryAction.label }}
        </router-link>
      </div>
    </section>

    <section class="grid gap-5 p-6">
      <AnnouncementCarousel />

      <div class="grid gap-3 md:grid-cols-3">
        <router-link
          v-for="card in workspaceCards"
          :key="card.label"
          :to="card.to"
          class="group rounded-lg border border-n-weak bg-n-alpha-1 p-4 transition hover:border-n-brand/50 hover:bg-n-alpha-2"
        >
          <div class="mb-5 flex items-start justify-between gap-3">
            <span class="text-sm font-medium text-n-slate-10">
              {{ card.label }}
            </span>
            <span
              class="grid size-9 place-items-center rounded-lg"
              :class="toneClass(card.tone)"
            >
              <span class="size-5" :class="card.icon" />
            </span>
          </div>
          <strong class="block text-xl font-semibold text-n-slate-12">
            {{ card.value }}
          </strong>
          <p class="mb-0 mt-2 text-sm leading-5 text-n-slate-11">
            {{ card.detail }}
          </p>
          <span
            class="mt-4 inline-flex items-center gap-1 text-sm font-medium text-n-slate-12"
          >
            Abrir
            <span
              class="i-lucide-arrow-up-right size-4 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </span>
        </router-link>
      </div>

      <div class="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,0.75fr)]">
        <section class="rounded-lg border border-n-weak bg-n-alpha-1 p-5">
          <div class="mb-5 flex items-start justify-between gap-3">
            <div>
              <h2 class="mb-1 text-base font-semibold text-n-slate-12">
                Preparação da conta
              </h2>
              <p class="mb-0 text-sm text-n-slate-11">
                Ajustes rápidos para deixar a operação pronta para uso.
              </p>
            </div>
            <span
              class="grid size-10 place-items-center rounded-lg bg-n-slate-3 text-n-slate-11"
            >
              <span class="i-lucide-list-checks size-5" />
            </span>
          </div>

          <div class="grid gap-3">
            <router-link
              v-for="item in visibleSetupItems"
              :key="item.label"
              :to="item.to"
              class="group flex items-center gap-3 rounded-lg border border-n-weak p-4 transition hover:border-n-brand/50 hover:bg-n-alpha-2"
            >
              <span
                class="grid size-10 shrink-0 place-items-center rounded-lg bg-n-alpha-2 text-n-slate-11"
              >
                <span class="size-5" :class="item.icon" />
              </span>
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-semibold text-n-slate-12">
                  {{ item.label }}
                </span>
                <span class="mt-1 block text-sm leading-5 text-n-slate-11">
                  {{ item.detail }}
                </span>
              </span>
              <span
                class="i-lucide-chevron-right size-4 shrink-0 text-n-slate-9 transition group-hover:text-n-slate-12"
              />
            </router-link>
          </div>
        </section>

        <aside class="rounded-lg border border-n-weak bg-n-alpha-1 p-5">
          <div class="mb-4 flex items-center gap-3">
            <span
              class="grid size-10 place-items-center rounded-lg bg-n-amber-3 text-n-amber-11"
            >
              <span class="i-lucide-lightbulb size-5" />
            </span>
            <div>
              <h2 class="mb-0 text-base font-semibold text-n-slate-12">
                Próxima melhor ação
              </h2>
              <p class="mb-0 text-xs text-n-slate-10">
                Sugerido para esta conta
              </p>
            </div>
          </div>

          <h3 class="mb-2 text-lg font-semibold text-n-slate-12">
            {{
              hasCorbanModule && canManageCorban
                ? 'Conectar o Corban'
                : 'Organizar os canais'
            }}
          </h3>
          <p class="mb-4 text-sm leading-6 text-n-slate-11">
            <template v-if="hasCorbanModule && canManageCorban">
              A conta tem módulos Corban, mas ainda não possui integração ativa.
              Configure as credenciais para liberar consultas e indicadores de
              crédito.
            </template>
            <template v-else>
              Comece pelos canais de atendimento e pela base de contatos. Essa
              conta pode operar normalmente sem o painel de crédito Corban.
            </template>
          </p>
          <router-link
            :to="primaryAction.to"
            class="inline-flex h-9 items-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
          >
            {{ primaryAction.label }}
            <span class="i-lucide-arrow-right size-4" />
          </router-link>
        </aside>
      </div>
    </section>
  </main>
</template>
